































import { Ref, Watch, Prop, Component, Vue } from 'vue-property-decorator'
import { isEqual } from 'lodash'
import { User } from '@/api/model/user'
import { Company } from '@/api/model/company'
import { commentToText, CommentText, mentionText, textToComment } from '@/helpers/comment-text'

@Component
export default class InputTextarea extends Vue {
    @Ref() element!: HTMLTextAreaElement
    @Prop({ type: Boolean, default: false }) readonly notNullable!: boolean
    @Prop({ type: Boolean, default: true }) readonly autoSize!: boolean
    @Prop({ type: Boolean, default: false }) readonly trim!: boolean
    @Prop({ type: Boolean, default: false }) readonly disabled: boolean
    @Prop({ default: null }) readonly rules!: string
    @Prop({ default: null }) readonly placeholder!: string
    @Prop({ default: '2' }) readonly rows!: string
    @Prop() readonly name!: string
    @Prop() readonly label!: string
    @Prop() readonly value!: string
    @Prop({ required: true }) readonly users!: User[]
    internalValue: string = null
    commentText: CommentText = null
    focus: boolean = false

    created() {
        this.onExternalChange()
        this.internalValue = this.transformInput(this.value)
    }

    transformInput(value: string) {
        if (this.trim && typeof value === 'string') {
            value = value.trim()
        }

        if (!value) {
            this.commentText = null

            return null
        }

        // The stored comment as text: decoded, mentions of known users as "@Name", line breaks as new lines
        this.commentText = commentToText(value, this.users)

        return this.commentText.text
    }

    transformOutput(value: string) {
        if (this.trim && typeof value === 'string') {
            value = value.trim()
        }

        if (!value) {
            return null
        }

        // The text escaped again, with the markup for "@Name" and new lines; unchanged, the stored comment as it was
        return textToComment(value, this.users, this.commentText)
    }

    @Watch('value')
    onExternalChange() {
        // Our own output coming back through v-model: keep the text as typed (an "&amp;" typed in the textarea
        // isn't decoded while typing)
        if (this.internalValue !== null && isEqual(this.value, this.transformOutput(this.internalValue))) {
            return
        }

        this.internalValue = this.transformInput(this.value)

        if (this.internalValue === null) {
            this.internalValue = ''
        }
    }

    @Watch('internalValue')
    onInternalChange() {
        if (this.autoSize) {
            const offset = this.element.offsetHeight - this.element.clientHeight
            this.element.style.height = 'auto'
            this.element.style.height = `${this.element.scrollHeight + offset}px`
        }

        if (this.notNullable === false && typeof this.internalValue === 'string' && this.internalValue.length === 0) {
            this.internalValue = null
        }

        const value = this.transformOutput(this.internalValue)
        if (!isEqual(this.value, value)) {
            this.$emit('input', value)
        }
    }

    onInput(): void {
        this.$emit('input', this.internalValue)
    }

    onFocus(): void {
        this.focus = true
    }

    onBlur(): void {
        this.focus = false
    }

    get componentClass(): object {
        return {
            'input-textarea': true,
            'input-textarea--focussed': this.focus,
            'input-textarea--disabled': this.disabled,
            'bd-round-outline': true,
        }
    }

    get mentionableItems(): Array<{ label: string, value: any, user: User }> {
        return this.users.map(user => ({
            label: user.name,
            // Inserted after the "@": the name with an invisible marker that keeps the user's id with it
            value: mentionText(user),
            user,
        }))
    }

    isCurrentCompany(company: Company): boolean {
        return this.$currentCompany.id === company.id
    }
}
